import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { GuardFallback } from './common/GuardFallback';

/**
 * Route guard for BRAND_ADMIN-only pages.
 * Renders children if the user is a Brand Admin, otherwise redirects.
 */
export const BrandAdminRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isBrandAdmin, user } = usePermissions();
  const { error, refetch } = useCurrentUser();

  if (!user) {
    return <GuardFallback error={error} onRetry={refetch} />;
  }

  if (!isBrandAdmin()) {
    return <Navigate to="/reports" replace />;
  }

  return children;
};

/**
 * Route guard for Data Exports (BRAND_ADMIN + ADMIN/SUPER_ADMIN).
 * Mirrors BrandAdminRoute; admins additionally need a brand selected in the
 * navbar switcher before the page can resolve a brandId (handled inside
 * the page itself — this guard only checks role).
 */
export const ExportsRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isBrandAdmin, isAdminOrHigher, user } = usePermissions();
  const { error, refetch } = useCurrentUser();

  if (!user) {
    return <GuardFallback error={error} onRetry={refetch} />;
  }

  if (!isBrandAdmin() && !isAdminOrHigher()) {
    return <Navigate to="/reports" replace />;
  }

  return children;
};

/**
 * Route guard for pages that require portal access (BRAND_ADMIN + BRAND_MANAGER).
 * FINANCE users cannot access portal routes.
 */
export const PortalRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { canAccessPortal, user } = usePermissions();
  const { error, refetch } = useCurrentUser();

  if (!user) {
    return <GuardFallback error={error} onRetry={refetch} />;
  }

  if (!canAccessPortal()) {
    return <Navigate to="/reports" replace />;
  }

  return children;
};

/**
 * Route guard for the shipment notice pages (/shipments, /shipments/new):
 * the roles that may file one (canShipToWarehouse, the API's ACTING_ROLES).
 * Not the v2 preview flag: brands reach these in production. FINANCE is sent
 * to /reports.
 */
export const ShipmentRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { canShipToWarehouse, user } = usePermissions();
  const { error, refetch } = useCurrentUser();

  if (!user) {
    return <GuardFallback error={error} onRetry={refetch} />;
  }

  if (!canShipToWarehouse()) {
    return <Navigate to="/reports" replace />;
  }

  return children;
};

/**
 * Route guard for finance pages (BRAND_ADMIN + FINANCE).
 * BRAND_MANAGER users cannot access P&L or reconciliation.
 */
export const FinanceRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { canAccessFinance, user } = usePermissions();
  const { error, refetch } = useCurrentUser();

  if (!user) {
    return <GuardFallback error={error} onRetry={refetch} />;
  }

  if (!canAccessFinance()) {
    return <Navigate to="/reports" replace />;
  }

  return children;
};
